/**
 * DektiteLinkInline (Iter233)
 * ───────────────────────────
 * "Has Penetration?" cascade for manual Flashing rows on the Components
 * tab. Mirrors the Annotator's Pin.Flashing → Pin.Penetration Dektite
 * link flow without ever touching the diagram.
 *
 * Behaviour:
 *   • "+ Add Penetration" button reveals an inline picker built from
 *     `PenetrationInlineCreate` (Kind / Usage / Ø / Variant cascade).
 *   • Picker auto-links a matching Dektite via /pipe-usages/{key}/auto-link
 *     — same endpoint the Annotator already uses.
 *   • Save → confirm dialog ("Add 1× DN50 Aluminium Dektite to Aquaseal
 *     section?") → on confirm the link is stamped on the Flashing row
 *     AND a paired Aquaseal/Dektite component_line is upserted via the
 *     parent's `onLinkDektite` callback (idempotent — increments qty).
 *   • Multiple links per Flashing supported (array on the line).
 *   • Each chip exposes Change · Unlink only · Unlink + delete actions.
 *
 * Parent contract:
 *   - `onLinkDektite(payload)`  → adds entry to `linked_dektites` AND
 *      upserts the matching Aquaseal line
 *   - `onUnlinkDektite(dektiteId, { deleteAquaseal })` → removes the
 *      entry; optionally decrements/removes the Aquaseal line
 *   - `onChangeDektite(oldDektiteId, payload)` → atomic swap
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { PenetrationInlineCreate } from '@/components/inline/PenetrationInlineCreate';

const variantLabel = (v) => {
  switch ((v || '').toLowerCase()) {
    case 'std':      return 'Std';
    case 'retrofit': return 'Retrofit';
    case 'lead':     return 'Lead-base';
    case 'ali':      return 'Ali-base';
    default:         return v || '—';
  }
};

export default function DektiteLinkInline({
  api,
  ref_id,                     // e.g. "Fl1" — for chip context
  roofType,
  roofProfile,
  linkedDektites = [],        // array of { dektite_id, dektite_name, supplier_code, kind, dia_mm, variant, qty }
  onLinkDektite,              // (payload) => void
  onUnlinkDektite,            // (dektite_id, { deleteAquaseal }) => void
  onChangeDektite,            // (old_dektite_id, payload) => void
}) {
  const [picking, setPicking] = useState(false);
  const [changingId, setChangingId] = useState(null);  // null = adding new; string = re-picking existing
  const [confirmPayload, setConfirmPayload] = useState(null);  // pending link, awaiting Aquaseal confirm

  // Build the payload from PenetrationInlineCreate's onSave fields.
  // Each linked entry carries a stable `uid` so multiple chips can
  // share the same `dektite_id` (idempotent design) without React key
  // collisions or "remove all" mishaps when the operator unlinks one.
  const buildPayload = (fields) => ({
    uid:            (crypto.randomUUID ? crypto.randomUUID() : `lk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
    dektite_id:     String(fields.linked_dektite_id || '').trim(),
    dektite_name:   String(fields.linked_dektite_name || '').trim(),
    supplier_code:  String(fields.linked_dektite_supplier_code || '').trim(),
    kind:           String(fields.penetration_kind || 'pipe'),
    dia_mm:         Number(fields.outlet_diameter_mm) || 0,
    variant:        String(fields.dektite_variant || ''),
    qty:            1,
  });

  const handlePickerSave = (cascadeFields) => {
    const payload = buildPayload(cascadeFields);
    if (!payload.dektite_id && !payload.dektite_name) {
      toast.error('No Dektite matched. Adjust Ø or variant and try again.');
      return;
    }
    // Open confirm dialog before stamping anything.
    setConfirmPayload(payload);
  };

  const confirmAdd = () => {
    if (!confirmPayload) return;
    if (changingId) {
      // changingId here is the uid of the entry being swapped.
      onChangeDektite?.(changingId, confirmPayload);
      toast.success(`Changed link to ${confirmPayload.dektite_name}`);
    } else {
      onLinkDektite?.(confirmPayload);
      toast.success(`Linked ${confirmPayload.dektite_name} — added to Dektite section`);
    }
    setConfirmPayload(null);
    setPicking(false);
    setChangingId(null);
  };

  const cancelConfirm = () => setConfirmPayload(null);

  const startChange = (entry) => {
    // We track the uid (not dektite_id) so the parent can swap the
    // EXACT entry — duplicate dektite_id chips coexist on the same row.
    setChangingId(entry.uid);
    setPicking(true);
  };

  const startAdd = () => {
    setChangingId(null);
    setPicking(true);
  };

  const handleUnlink = (entry, deleteAquaseal) => {
    // Same Dektite linked multiple times → each entry contributes qty=1
    // to the Aquaseal line. Removing this chip should remove ONE entry
    // (by uid) and decrement Aquaseal qty by 1 when requested.
    onUnlinkDektite?.(entry.uid, { deleteAquaseal, dektite_id: entry.dektite_id });
    toast.success(deleteAquaseal
      ? `Unlinked & removed 1× ${entry.dektite_name} from Dektite section`
      : `Unlinked ${entry.dektite_name} (Dektite line untouched)`);
  };

  return (
    <div
      className="col-span-12 w-full"
      data-testid={`comp-line-dektite-link-${ref_id}`}
    >
      <div className="flex items-center gap-2 flex-wrap pt-1">
        <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-700 shrink-0">
          Penetrations
        </span>

        {/* Existing link chips */}
        {linkedDektites.length === 0 && !picking && (
          <span className="text-[10px] text-slate-400 italic">None — flashing has no penetration</span>
        )}
        {linkedDektites.map((entry, idx) => {
          // Each linked entry carries a stable uid (set at link time) so
          // duplicate-Dektite chips don't collide on React keys and the
          // unlink handler can remove the EXACT entry by uid.
          const uid = entry.uid || `${idx}-${entry.dektite_id || entry.dektite_name}`;
          return (
          <div
            key={uid}
            data-testid={`dektite-link-chip-${ref_id}-${uid}`}
            className="inline-flex items-center gap-1 bg-emerald-100 ring-1 ring-emerald-300 rounded-full pl-2 pr-1 py-0.5 text-[10px]"
            title={`Linked Dektite. Kind: ${entry.kind || '—'} · Ø${entry.dia_mm || '?'} · ${variantLabel(entry.variant)}`}
          >
            <span className="font-semibold text-emerald-900">
              🔗 {entry.dektite_name || `Ø${entry.dia_mm} ${variantLabel(entry.variant)}`}
            </span>
            {entry.supplier_code && (
              <span className="font-mono-num text-[9px] text-emerald-700">{entry.supplier_code}</span>
            )}
            <button
              type="button"
              data-testid={`dektite-link-change-${ref_id}-${uid}`}
              onClick={() => startChange(entry)}
              className="ml-1 px-1 py-0.5 text-[9px] font-bold rounded text-amber-800 bg-amber-100 hover:bg-amber-200"
              title="Change which Dektite is linked"
            >✎</button>
            <button
              type="button"
              data-testid={`dektite-link-unlink-${ref_id}-${uid}`}
              onClick={() => handleUnlink(entry, false)}
              className="px-1 py-0.5 text-[9px] font-bold rounded text-slate-700 bg-white hover:bg-slate-100 ring-1 ring-slate-200"
              title="Unlink only — Dektite line stays"
            >Unlink</button>
            <button
              type="button"
              data-testid={`dektite-link-delete-${ref_id}-${uid}`}
              onClick={() => handleUnlink(entry, true)}
              className="px-1 py-0.5 text-[9px] font-bold rounded text-rose-700 bg-rose-50 hover:bg-rose-100 ring-1 ring-rose-200"
              title="Unlink AND remove 1× from the Dektite section"
            >🗑</button>
          </div>
          );
        })}

        {/* Add button */}
        {!picking && (
          <button
            type="button"
            data-testid={`dektite-link-add-${ref_id}`}
            onClick={startAdd}
            className="px-2 py-1 text-[10px] font-bold rounded text-emerald-800 bg-emerald-50 hover:bg-emerald-100 ring-1 ring-emerald-200"
            title="Pick a Dektite to link to this flashing — auto-adds it to the Dektite section"
          >+ Add Penetration</button>
        )}

        {picking && (
          <button
            type="button"
            data-testid={`dektite-link-cancel-${ref_id}`}
            onClick={() => { setPicking(false); setChangingId(null); }}
            className="px-2 py-1 text-[10px] font-bold rounded text-slate-600 bg-white hover:bg-slate-50 ring-1 ring-slate-300"
          >Cancel</button>
        )}
      </div>

      {/* Inline picker — reuses Annotator's PenetrationInlineCreate verbatim */}
      {picking && (
        <div className="mt-2" data-testid={`dektite-link-picker-${ref_id}`}>
          <PenetrationInlineCreate
            api={api}
            defaultKind="pipe"
            roofProfile={roofProfile}
            roofType={roofType}
            onSave={handlePickerSave}
            onCancel={() => { setPicking(false); setChangingId(null); }}
          />
        </div>
      )}

      {/* Confirmation overlay */}
      {confirmPayload && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60"
          data-testid={`dektite-link-confirm-backdrop-${ref_id}`}
          onClick={cancelConfirm}
        >
          <div
            className="bg-white rounded-lg shadow-2xl ring-1 ring-slate-300 w-[440px] max-w-[95vw]"
            onClick={(e) => e.stopPropagation()}
            data-testid={`dektite-link-confirm-dialog-${ref_id}`}
          >
            <div className="px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-bold text-slate-800">
                {changingId ? 'Change linked Dektite?' : 'Add Dektite to Dektite section?'}
              </h3>
              <p className="text-[11px] text-slate-500 mt-1">
                {changingId
                  ? 'The old Dektite line will be decremented and the new one added.'
                  : 'The Dektite will be linked to this flashing AND a matching Dektite line will be added (or its qty incremented if one already exists).'}
              </p>
            </div>
            <div className="px-4 py-3 space-y-1">
              <div className="text-[12px]">
                <strong>{confirmPayload.dektite_name}</strong>
                {confirmPayload.supplier_code && (
                  <span className="ml-2 font-mono-num text-[10px] text-slate-500">{confirmPayload.supplier_code}</span>
                )}
              </div>
              <div className="text-[11px] text-slate-600">
                Kind: <strong>{confirmPayload.kind}</strong> · Ø{confirmPayload.dia_mm} · Variant: <strong>{variantLabel(confirmPayload.variant)}</strong>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50 rounded-b-lg">
              <button
                type="button"
                onClick={cancelConfirm}
                className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white hover:bg-slate-100 ring-1 ring-slate-300 rounded"
                data-testid={`dektite-link-confirm-cancel-${ref_id}`}
              >Cancel</button>
              <button
                type="button"
                onClick={confirmAdd}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded"
                data-testid={`dektite-link-confirm-accept-${ref_id}`}
              >{changingId ? 'Change link' : 'Add to Dektite'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
